import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  ChevronDown,
  Maximize2,
  MessageSquare,
  Minimize2,
  WifiOff,
} from "lucide-react";
import { useChatSocket } from "../../backend/chat/ChatSocketProvider";
import { useConversations, useUnreadCount } from "../../backend/queries/chat";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { ConversationList } from "./ConversationList";
import { ConversationPane, titleForConversation } from "./ConversationPane";
import { unreadLabel } from "./chatFormat";
import { IncomingMessageAnnouncer } from "./IncomingMessageAnnouncer";

export type DockState = "collapsed" | "open" | "maximised";

type ChatDockProps = {
  currentUserId: string | null | undefined;
  /** Controlled so the TopBar trigger can open the dock. */
  state: DockState;
  onStateChange: (state: DockState) => void;
};

/**
 * Floating chat dock, bottom right, for clients.
 *
 * Clients have no full-page inbox, so the dock carries a maximised state: a
 * long thread has to stay readable without leaving the page it is docked on.
 */
export function ChatDock({
  currentUserId,
  state,
  onStateChange,
}: ChatDockProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const setState = onStateChange;

  const open = state !== "collapsed";
  const conversations = useConversations({ enabled: open });
  const unread = useUnreadCount();
  const { status, reconnect } = useChatSocket();

  // The dock is client-only, and a client's project conversation is usually
  // the only one they need, so it leads the list rather than sorting purely
  // by recency. See docs/chat.md, "Project-linked conversations".
  const sortedConversations = useMemo(() => {
    const conversations_ = conversations.data ?? [];
    return [...conversations_].sort((left, right) => {
      const leftIsProject = left.project_id != null ? 0 : 1;
      const rightIsProject = right.project_id != null ? 0 : 1;
      return leftIsProject - rightIsProject;
    });
  }, [conversations.data]);

  const selected = useMemo(
    () =>
      conversations.data?.find(
        (conversation) => conversation.id === selectedId,
      ) ?? undefined,
    [conversations.data, selectedId],
  );

  // A single conversation is the common client case, so skip the list and open
  // it directly rather than making them click through a list of one.
  useEffect(() => {
    if (!open || selectedId) return;
    const only = conversations.data;
    if (only?.length === 1) setSelectedId(only[0].id);
  }, [conversations.data, open, selectedId]);

  const close = useCallback(() => {
    setState("collapsed");
    setSelectedId(null);
  }, [setState]);

  const unreadCount = unread.data ?? 0;

  // The dock is non-modal: the page behind it stays usable, so it does not trap
  // focus. Focus still has to follow it. Opening moves focus into the dock, and
  // closing puts it back on the launcher, because each replaces the other in
  // the page and the control that held focus is gone.
  const dockRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open === wasOpen.current) return;
    wasOpen.current = open;
    (open ? dockRef.current : launcherRef.current)?.focus();
  }, [open]);

  if (!open) {
    return (
      <div className="fixed bottom-4 right-4 z-50 print:hidden">
        {/* The launcher's label changes with the count, but a label only
            speaks when the button has focus. This speaks when it does not. */}
        <span role="status" className="sr-only">
          {unreadCount
            ? `${unreadCount} unread message${unreadCount === 1 ? "" : "s"}`
            : ""}
        </span>
        <Button
          ref={launcherRef}
          type="button"
          size="icon"
          onClick={() => setState("open")}
          aria-label={
            unreadCount
              ? `Open messages, ${unreadCount} unread`
              : "Open messages"
          }
          className="relative h-14 w-14 rounded-full shadow-soft"
        >
          <MessageSquare className="h-6 w-6" aria-hidden="true" />
          {unreadCount > 0 ? (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 min-w-5 justify-center px-1.5 py-0 text-[11px] leading-5"
            >
              {unreadLabel(unreadCount)}
            </Badge>
          ) : null}
        </Button>
      </div>
    );
  }

  const maximised = state === "maximised";

  return (
    <div
      ref={dockRef}
      tabIndex={-1}
      className={cn(
        "fixed z-50 flex flex-col overflow-hidden rounded-lg border bg-card shadow-soft outline-none print:hidden",
        maximised
          ? "inset-2 sm:inset-6"
          : // Capped to the screen, or at 200 percent zoom the header, with
            // the close control, sits above the top edge where it cannot be reached.
            "bottom-4 right-4 h-[32rem] max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-sm",
      )}
      role="dialog"
      aria-label="Messages"
    >
      <IncomingMessageAnnouncer
        conversations={conversations.data}
        currentUserId={currentUserId}
        titleFor={(conversation) =>
          titleForConversation(conversation, currentUserId)
        }
      />
      <header className="flex items-center gap-2 border-b bg-card px-3 py-2">
        {selected ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="min-h-touch min-w-touch shrink-0"
            onClick={() => setSelectedId(null)}
            aria-label="Back to conversations"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : null}

        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold">
            {selected
              ? titleForConversation(selected, currentUserId)
              : "Messages"}
          </h2>
          {selected?.type === "group" ? (
            <p className="truncate text-xs text-muted-foreground">
              {selected.participants.length} participant
              {selected.participants.length === 1 ? "" : "s"}
            </p>
          ) : null}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="min-h-touch min-w-touch shrink-0"
          onClick={() => setState(maximised ? "open" : "maximised")}
          aria-label={
            maximised ? "Restore message window" : "Maximise message window"
          }
        >
          {maximised ? (
            <Minimize2 className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="min-h-touch min-w-touch shrink-0"
          onClick={close}
          aria-label="Close messages"
        >
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </Button>
      </header>

      {status !== "open" ? (
        <div
          role="status"
          className="flex items-center gap-2 border-b bg-muted px-3 py-1.5 text-xs text-muted-foreground"
        >
          <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            {status === "connecting"
              ? "Connecting to live messages"
              : "You are offline. New messages may be delayed."}
          </span>
          {status === "offline" ? (
            <button
              type="button"
              onClick={reconnect}
              className="font-medium underline underline-offset-2"
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      {selected ? (
        <ConversationPane
          conversationId={selected.id}
          conversation={selected}
          currentUserId={currentUserId}
          className="min-h-0"
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ConversationList
            conversations={sortedConversations}
            titleFor={(conversation) =>
              titleForConversation(conversation, currentUserId)
            }
            selectedId={selectedId}
            onSelect={setSelectedId}
            currentUserId={currentUserId}
            isLoading={conversations.isPending}
            error={conversations.error as Error | null}
            onRetry={() => void conversations.refetch()}
            emptyMessage="You have no messages yet. Your relocation agent will be in touch here."
          />
        </div>
      )}
    </div>
  );
}
